import { unified } from 'unified';
import remarkParse from 'remark-parse';
import GithubSlugger from 'github-slugger';
import { visit } from 'unist-util-visit';
import type { Root, Heading, PhrasingContent } from 'mdast';

/**
 * One entry in a document outline / table of contents.
 *
 * - `depth`  — heading level, 1 (`#`) … 6 (`######`).
 * - `text`   — the heading's plain-text content (markdown stripped).
 * - `slug`   — the GitHub-style anchor slug. It is generated with the SAME
 *   `github-slugger` algorithm and document-order pass that `rehype-slug` uses
 *   in the preview, so it MATCHES the rendered heading's `id` (including
 *   duplicate disambiguation suffixes like `-1`, `-2`).
 * - `line`   — 1-based source line of the heading (from mdast `position`),
 *   used to jump the editor / drive content-aware scroll sync.
 */
export interface HeadingItem {
  depth: 1 | 2 | 3 | 4 | 5 | 6;
  text: string;
  slug: string;
  line: number;
}

/** Concatenate the plain-text content of a heading's inline children. */
function headingText(node: Heading): string {
  let out = '';
  const collect = (children: PhrasingContent[]): void => {
    for (const child of children) {
      if (child.type === 'text' || child.type === 'inlineCode') {
        out += child.value;
      } else if ('children' in child && Array.isArray(child.children)) {
        collect(child.children as PhrasingContent[]);
      }
    }
  };
  collect(node.children);
  return out;
}

/**
 * Parse `markdown` and return its headings as an ordered outline.
 *
 * Slugs are produced by a SINGLE `GithubSlugger` instance walked over the
 * headings in document order — identical to how `rehype-slug` slugs preview
 * headings — so duplicate headings get the same disambiguation (`-1`, `-2`, …)
 * and the outline can link straight to the preview's heading `id`s.
 *
 * Pure (no DOM / side effects); safe to run in `core`.
 */
export function extractHeadings(markdown: string): HeadingItem[] {
  const tree = unified().use(remarkParse).parse(markdown) as Root;
  const slugger = new GithubSlugger();
  const headings: HeadingItem[] = [];

  visit(tree, 'heading', (node: Heading) => {
    const text = headingText(node);
    headings.push({
      depth: node.depth,
      text,
      slug: slugger.slug(text),
      line: node.position?.start?.line ?? 0,
    });
  });

  return headings;
}
